import { Phone, MapPin, Instagram, Clock } from "lucide-react";
import { RESTAURANT_INFO } from "../data/menu";
import { useOpenStatus } from "../hooks/useOpenStatus";

const WhatsAppIcon = ({ size = 20 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M20.52 3.48A11.85 11.85 0 0 0 12.04 0C5.5 0 .2 5.3.2 11.84c0 2.08.55 4.12 1.6 5.92L0 24l6.4-1.68a11.83 11.83 0 0 0 5.64 1.44h.01c6.54 0 11.84-5.3 11.84-11.84 0-3.16-1.23-6.13-3.37-8.44Zm-8.48 18.2h-.01a9.8 9.8 0 0 1-5-1.37l-.36-.21-3.8 1 1.02-3.7-.23-.38a9.83 9.83 0 0 1-1.5-5.18c0-5.43 4.42-9.85 9.86-9.85a9.78 9.78 0 0 1 6.97 2.88 9.78 9.78 0 0 1 2.89 6.97c0 5.43-4.43 9.84-9.84 9.84Zm5.4-7.36c-.3-.15-1.75-.86-2.02-.96-.27-.1-.46-.15-.66.15-.2.3-.76.96-.94 1.16-.17.2-.34.22-.64.07-.3-.15-1.26-.46-2.4-1.47-.88-.78-1.48-1.75-1.65-2.05-.17-.3-.02-.46.13-.6.13-.13.3-.34.44-.5.15-.18.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.9-2.18-.24-.57-.48-.5-.66-.5l-.56-.01a1.07 1.07 0 0 0-.78.37c-.27.3-1.02 1-1.02 2.44 0 1.44 1.05 2.83 1.2 3.03.15.2 2.07 3.17 5.02 4.45.7.3 1.25.48 1.68.62.7.22 1.34.19 1.85.12.56-.08 1.75-.71 2-1.4.25-.7.25-1.28.17-1.4-.07-.13-.27-.2-.57-.35Z" />
  </svg>
);

export const Contact = () => {
  const isOpen = useOpenStatus(
    RESTAURANT_INFO.hours.openHour,
    RESTAURANT_INFO.hours.closeHour,
  );

  return (
    <section
      id="contacto"
      data-testid="contact-section"
      className="relative bg-black text-white py-16 sm:py-24 pb-28 sm:pb-24"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-5">
          <span className="h-px w-10 bg-red-600" />
          <span className="text-[11px] uppercase tracking-[0.35em] text-red-500 font-semibold">
            Visítenos
          </span>
        </div>
        <h2 className="font-display uppercase text-5xl sm:text-6xl leading-[0.95] tracking-tighter mb-12">
          Estamos para <br />
          <span className="text-red-600">servirle.</span>
        </h2>

        <div className="grid lg:grid-cols-12 gap-6 lg:gap-10">
          {/* Info column */}
          <div className="lg:col-span-5 space-y-4">
            <div
              data-testid="contact-open-status"
              className="flex items-center gap-3 border border-white/10 px-5 py-4 bg-white/[0.02]"
            >
              <span className="relative flex h-3 w-3">
                <span
                  className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${
                    isOpen ? "bg-green-500 animate-ping" : "bg-red-500"
                  }`}
                />
                <span
                  className={`relative inline-flex rounded-full h-3 w-3 ${
                    isOpen ? "bg-green-500" : "bg-red-500"
                  }`}
                />
              </span>
              <div>
                <p className="text-sm uppercase tracking-widest font-semibold text-white">
                  {isOpen ? "Abierto ahora" : "Cerrado"}
                </p>
                <p className="text-xs text-white/60 flex items-center gap-1 mt-0.5">
                  <Clock size={12} />
                  {RESTAURANT_INFO.hours.label}
                </p>
              </div>
            </div>

            <a
              href={RESTAURANT_INFO.mapsUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-address-button"
              className="block border border-white/10 hover:border-red-600 px-5 py-5 transition-colors group"
            >
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 flex items-center justify-center bg-red-600 text-white shrink-0">
                  <MapPin size={20} strokeWidth={2.5} />
                </span>
                <div className="min-w-0">
                  <p className="text-[11px] uppercase tracking-[0.25em] text-white/50">
                    Dirección
                  </p>
                  <p className="text-white text-base mt-1 group-hover:text-red-500 transition-colors">
                    {RESTAURANT_INFO.address}
                  </p>
                </div>
              </div>
            </a>

            <a
              href={`https://wa.me/${RESTAURANT_INFO.whatsapp}?text=${encodeURIComponent(
                "Hola, quiero hacer un pedido en Mariscos Popeye 1985.",
              )}`}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp-button"
              className="block border border-white/10 hover:border-red-600 px-5 py-5 transition-colors group"
            >
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 flex items-center justify-center bg-[#25D366] text-white shrink-0">
                  <WhatsAppIcon size={20} />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-white/50">
                    WhatsApp · Pedidos
                  </p>
                  <p className="text-white text-base mt-1 group-hover:text-red-500 transition-colors">
                    {RESTAURANT_INFO.whatsappDisplay}
                  </p>
                </div>
              </div>
            </a>

            <a
              href={`tel:+52${RESTAURANT_INFO.phone}`}
              data-testid="contact-phone-button"
              className="block border border-white/10 hover:border-red-600 px-5 py-5 transition-colors group"
            >
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 flex items-center justify-center bg-red-600 text-white shrink-0">
                  <Phone size={20} strokeWidth={2.5} />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-white/50">
                    Teléfono
                  </p>
                  <p className="text-white text-base mt-1 group-hover:text-red-500 transition-colors">
                    {RESTAURANT_INFO.phoneDisplay}
                  </p>
                </div>
              </div>
            </a>

            <a
              href={RESTAURANT_INFO.instagramUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-instagram-button"
              className="block border border-white/10 hover:border-red-600 px-5 py-5 transition-colors group"
            >
              <div className="flex items-start gap-4">
                <span className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-[#feda75] via-[#d62976] to-[#4f5bd5] text-white shrink-0">
                  <Instagram size={20} strokeWidth={2.5} />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.25em] text-white/50">
                    Instagram
                  </p>
                  <p className="text-white text-base mt-1 group-hover:text-red-500 transition-colors">
                    @{RESTAURANT_INFO.instagram}
                  </p>
                </div>
              </div>
            </a>
          </div>

          {/* Map column */}
          <div
            data-testid="contact-map"
            className="lg:col-span-7 border border-white/10 overflow-hidden min-h-[400px] lg:min-h-full"
          >
            <iframe
              title="Ubicación Mariscos Popeye 1985"
              src={RESTAURANT_INFO.mapsEmbed}
              className="w-full h-full min-h-[400px]"
              style={{ border: 0, filter: "grayscale(0.4) contrast(1.05)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </div>
      </div>
    </section>
  );
};
